import React, { useState, useMemo } from 'react';
import { Search, Download } from 'lucide-react';
import { sans, mono, green, greenLight, useTheme } from '../../tokens';

const inputStyle = (t) => ({
  fontFamily: sans,
  fontSize: 12,
  padding: '8px 12px',
  border: '1px solid ' + t.border,
  borderRadius: 6,
  background: t.inputBg,
  color: t.textPrimary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

const labelStyle = (t) => ({
  fontSize: 11,
  fontWeight: 600,
  color: t.textSecondary,
  fontFamily: sans,
  marginBottom: 6,
  display: 'block',
});

const sectionTitleStyle = (t) => ({
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: t.textMuted,
  fontFamily: sans,
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid ' + t.border,
});

const toggleStyle = (enabled, t) => ({
  width: 36,
  height: 20,
  borderRadius: 10,
  background: enabled ? green : t.border,
  position: 'relative',
  cursor: 'pointer',
  transition: 'background 0.15s',
  flexShrink: 0,
});

const toggleKnob = (enabled) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#fff',
  position: 'absolute',
  top: 2,
  left: enabled ? 18 : 2,
  transition: 'left 0.15s',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
});

const LOGGED_EVENTS = [
  { key: 'dataChanges', label: 'Data changes' },
  { key: 'logins', label: 'Logins' },
  { key: 'roleChanges', label: 'Role changes' },
  { key: 'periodLockUnlock', label: 'Period lock / unlock' },
  { key: 'capTableChanges', label: 'Cap table changes' },
  { key: 'documentUploads', label: 'Document uploads' },
  { key: 'exports', label: 'Exports' },
  { key: 'settingsChanges', label: 'Settings changes' },
  { key: 'syncEvents', label: 'Sync events' },
];

const SAMPLE_AUDIT = [
  { id: 1, timestamp: '2026-03-30 09:15:22', user: 'Taylor Brooks', module: 'Close', action: 'Period closed', details: 'February 2026 period marked as closed' },
  { id: 2, timestamp: '2026-03-29 14:32:11', user: 'Jordan Lee', module: 'Equity', action: 'Grant created', details: 'Option grant EQ-2026-042 created for S. Rivera' },
  { id: 3, timestamp: '2026-03-28 16:05:44', user: 'Taylor Brooks', module: 'Settings', action: 'Role updated', details: 'Casey Nguyen role changed from Preparer to Reviewer' },
  { id: 4, timestamp: '2026-03-28 10:20:18', user: 'System', module: 'Integrations', action: 'Sync completed', details: 'Sage Intacct GL sync: 145 transactions imported' },
  { id: 5, timestamp: '2026-03-27 11:45:33', user: 'Sam Rivera', module: 'Close', action: 'Document uploaded', details: 'Bank reconciliation Feb 2026 uploaded' },
  { id: 6, timestamp: '2026-03-26 09:30:15', user: 'Taylor Brooks', module: 'Financial', action: 'Account added', details: 'GL 6200 - Professional Fees added to chart of accounts' },
  { id: 7, timestamp: '2026-03-25 15:12:08', user: 'Casey Nguyen', module: 'Close', action: 'Export generated', details: 'Monthly close package exported as PDF' },
  { id: 8, timestamp: '2026-03-24 08:55:42', user: 'Taylor Brooks', module: 'Equity', action: 'Cap table updated', details: 'Series A-2 preferred shares added: 500,000 shares' },
];

const MODULES_FILTER = ['All', 'Close', 'Equity', 'Settings', 'Integrations', 'Financial'];
const USERS_FILTER = ['All', 'Taylor Brooks', 'Jordan Lee', 'Sam Rivera', 'Casey Nguyen', 'System'];

export default function AuditTrailSettings() {
  const t = useTheme();
  const savedAudit = JSON.parse(localStorage.getItem('finkloz_audit_settings') || 'null');
  const [eventToggles, setEventToggles] = useState(
    savedAudit?.eventToggles || Object.fromEntries(LOGGED_EVENTS.map(e => [e.key, true]))
  );
  const [retention, setRetention] = useState(savedAudit?.retention || '7');
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('All');
  const [userFilter, setUserFilter] = useState('All');
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const markDirty = () => setDirty(true);

  const handleSave = () => {
    localStorage.setItem('finkloz_audit_settings', JSON.stringify({
      eventToggles, retention,
    }));
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const filteredAudit = useMemo(() => {
    let items = SAMPLE_AUDIT;
    if (moduleFilter !== 'All') {
      items = items.filter(a => a.module === moduleFilter);
    }
    if (userFilter !== 'All') {
      items = items.filter(a => a.user === userFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(a =>
        a.action.toLowerCase().includes(q) ||
        a.details.toLowerCase().includes(q) ||
        a.user.toLowerCase().includes(q)
      );
    }
    return items;
  }, [search, moduleFilter, userFilter]);

  const handleExport = () => {
    const header = 'Timestamp,User,Module,Action,Details\n';
    const rows = filteredAudit.map(a =>
      `"${a.timestamp}","${a.user}","${a.module}","${a.action}","${a.details}"`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'audit_log.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Audit Trail
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Configure logged events, retention policies, and review the audit log.
      </p>

      {/* Logged Events */}
      <div style={sectionTitleStyle(t)}>Logged Events</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
        {LOGGED_EVENTS.map(evt => (
          <div key={evt.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              onClick={() => {
                setEventToggles(prev => ({ ...prev, [evt.key]: !prev[evt.key] }));
                markDirty();
              }}
              style={toggleStyle(eventToggles[evt.key], t)}
            >
              <div style={toggleKnob(eventToggles[evt.key])} />
            </div>
            <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: sans }}>{evt.label}</span>
          </div>
        ))}
      </div>

      {/* Retention */}
      <div style={{ marginBottom: 32 }}>
        <label style={labelStyle(t)}>Retention Period</label>
        <select
          value={retention}
          onChange={e => { setRetention(e.target.value); markDirty(); }}
          style={{ ...inputStyle(t), maxWidth: 300 }}
        >
          <option value="1">1 year</option>
          <option value="3">3 years</option>
          <option value="5">5 years</option>
          <option value="7">7 years</option>
          <option value="10">10 years</option>
        </select>
      </div>

      {/* Audit Log Viewer */}
      <div style={sectionTitleStyle(t)}>Audit Log</div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: t.textMuted }} />
          <input
            type="text"
            placeholder="Search audit log..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle(t), paddingLeft: 32 }}
          />
        </div>
        <select
          value={moduleFilter}
          onChange={e => setModuleFilter(e.target.value)}
          style={{ ...inputStyle(t), width: 'auto', maxWidth: 160 }}
        >
          {MODULES_FILTER.map(m => (
            <option key={m} value={m}>{m === 'All' ? 'All Modules' : m}</option>
          ))}
        </select>
        <select
          value={userFilter}
          onChange={e => setUserFilter(e.target.value)}
          style={{ ...inputStyle(t), width: 'auto', maxWidth: 200 }}
        >
          {USERS_FILTER.map(u => (
            <option key={u} value={u}>{u === 'All' ? 'All Users' : u}</option>
          ))}
        </select>
        <button
          onClick={handleExport}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', border: '1px solid ' + t.border, borderRadius: 6,
            background: 'transparent', color: t.textSecondary, fontSize: 12,
            fontWeight: 600, fontFamily: sans, cursor: 'pointer',
          }}
        >
          <Download size={14} />
          Export CSV
        </button>
      </div>

      <div style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 32,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
          <thead>
            <tr style={{ background: t.bgSurface }}>
              {['Timestamp', 'User', 'Module', 'Action', 'Details'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px',
                  textAlign: 'left',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: t.textMuted,
                  borderBottom: '1px solid ' + t.border,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAudit.map(a => (
              <tr key={a.id} style={{ borderBottom: '1px solid ' + t.borderLight }}>
                <td style={{ padding: '10px 14px', color: t.textSecondary, fontFamily: mono, fontSize: 11, whiteSpace: 'nowrap' }}>
                  {a.timestamp}
                </td>
                <td style={{ padding: '10px 14px', color: t.textPrimary, fontWeight: 500 }}>
                  {a.user}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: 0.3,
                    background: t.pillBg, color: t.textSecondary,
                  }}>
                    {a.module}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', color: t.textPrimary, fontWeight: 500 }}>
                  {a.action}
                </td>
                <td style={{ padding: '10px 14px', color: t.textSecondary, fontSize: 11 }}>
                  {a.details}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!dirty}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 6,
            background: dirty ? green : t.border,
            color: dirty ? '#fff' : t.textMuted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: sans,
            cursor: dirty ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          Save
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: green, fontFamily: sans, fontWeight: 500 }}>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
